import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailItemExtra,
  InsertHrailItemExtraBody,
  UpdateHrailItemExtraBody,
} from "./types/itemExtras";

export const useHrailItemExtrasApi = () => {
  const api = useApi();

  const getAdminHrailItemExtra = useCallback(
    (itemExtraId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/item_extras/${itemExtraId}`;
      return useSWRMutation<HrailItemExtra, CustomError, string>(url, (url) =>
        api.get<HrailItemExtra>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailItemExtras = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/item_extras`, query);
      return useSWRImmutable<Paginated<HrailItemExtra>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailItemExtra>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailItemExtras = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/item_extras`, query);
      return api.get<Paginated<HrailItemExtra>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailItemExtra = useCallback(
    async (body: InsertHrailItemExtraBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailItemExtraBody>(
        `/admin/settings/hrail/item_extras`,
        body,
      );
      mutateCache([`/admin/settings/hrail/item_extras`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailItemExtra = useCallback(
    async (itemExtraId: string, body: UpdateHrailItemExtraBody): Promise<void> => {
      const url = `/admin/settings/hrail/item_extras/${itemExtraId}`;
      await api.put<UpdateHrailItemExtraBody>(url, body);
      mutateCache([`/admin/settings/hrail/item_extras`], url);
    },
    [api],
  );

  const deleteAdminHrailItemExtra = useCallback(
    async (itemExtraId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/item_extras/${itemExtraId}`);
      mutateCache([`/admin/settings/hrail/item_extras`]);
    },
    [api],
  );

  return {
    getAdminHrailItemExtra,
    useAdminHrailItemExtras,
    getAdminHrailItemExtras,
    insertAdminHrailItemExtra,
    updateAdminHrailItemExtra,
    deleteAdminHrailItemExtra,
  };
};
